import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AppConfig } from '@/lib/config';
import { apiRoutes } from '@/lib/routes/api';
import type { ConsoleData } from '@/lib/api';
import type { ExerciseRunResponse } from '@/lib/api/v2';
import type { IExerciseInfrastructure } from '@/types/scenarios';

export interface RoleDto {
  id: number;
  code: string;
  name: string;
  is_active?: boolean;
  color_code?: string;
}

/** GET /api/users/ list item (paginated leaderboard) */
export interface UserListItemDto {
  id: number;
  email: string;
  username?: string;
  full_name?: string;
  /** Current API uses `team` (e.g. "Red Team"); `role` kept for older responses */
  team?: string | null;
  role?: string | null;
  is_active: boolean;
  is_verified?: boolean;
  date_joined: string;
  last_login?: string | null;
  updated_at: string;
  avatar?: { image_url?: string } | null;
}

export interface UsersListResponseDto {
  count?: number;
  total_pages?: number;
  current_page?: number;
  next?: string | null;
  previous?: string | null;
  results?: UserListItemDto[];
}

export interface CategoryDto {
  id: number;
  name: string;
  slug: string | null;
  parent?: number | null;
  is_published: boolean;
  is_featured: boolean;
  is_disabled: boolean;
  order: number;
  /** Scenarios still pointing at this category. */
  exercise_count?: number;
  /** Child categories that use this category as their parent. */
  child_count?: number;
}

export interface CategoryListResponseDto {
  results: CategoryDto[];
  count?: number;
  next?: string | null;
  previous?: string | null;
  current_page?: number;
  total_pages?: number;
}

export interface ScenarioListItemDto {
  id: string;
  name: string;
  description: string;
  ttx_enabled?: boolean;
  is_approved?: boolean;
  is_prepared?: boolean;
  category_name?: string;
  type_name?: string;
  severity_name?: string;
  thumbnail_url?: string;
  updated_at?: string;
}

export interface ScenarioListResponseDto {
  results: ScenarioListItemDto[];
  count?: number;
  next?: string | null;
  previous?: string | null;
  current_page?: number;
  total_pages?: number;
}

export interface ScenarioDetailDto {
  id: string;
  name: string;
  /** Backend exercise detail flag: true when any run is active (launching/launched/started/paused). */
  has_active_run?: boolean;
  description: string;
  objective: string;
  prerequisite: string;
  thumbnail_url: string;
  ttx_enabled?: boolean;
  is_approved?: boolean;
  is_prepared?: boolean;
  category: { id: string; name: string };
  type: { id: string; name: string };
  severity: string;
  mode?: { id: string; name: string; code: string };
  creator: { id: string; full_name: string };
  machine_names: string[];
  phases?: Array<{
    id: number;
    name: string;
    order: number;
    slug: string;
    thumbnail_url?: string | null;
  }>;
  exercise_phases?: Array<{
    id: number;
    uuid: string;
    exercise: string;
    phase: number;
    order: number;
    duration_mins: number;
    created_at?: string;
    updated_at?: string;
  }>;
  walkthrough_files?: Array<{
    id: number;
    exercise: string;
    team: number;
    exercise_team: number | null;
    title: string;
    file: string;
    file_url: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
  }>;
  files_data?: {
    red_team?: string[];
    blue_team?: string[];
    purple_team?: string[];
    yellow_team?: string[];
  };
  flags?: Array<{
    flag_id: string;
    map_id: number;
    order: number;
    is_required: boolean;
    question: string;
    answer: string;
    score: number;
    team: number | null;
    team_name?: string;
    hint: string;
    hint_penalty: number | null;
    placeholder: string;
    exercise_team_mapping: {
      id: number;
      team: number;
      team_name: string;
      team_code: string;
      vm_required: boolean;
      is_active: boolean;
    } | null;
    phase: {
      id: number;
      name: string;
      slug: string;
      order: number;
    } | null;
  }>;
  exercise_team_mappings?: Array<{
    id: number;
    exercise: string;
    team: number;
    team_name: string;
    team_code: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
  }>;
  infrastructure?: IExerciseInfrastructure;
  hardware_details?: {
    vcpu?: number;
    disk_size_gb?: number;
    ram_gb?: number;
    vm_count?: number;
  };
  points: number;
  created_at: string;
  updated_at: string;
}

/** Same shape as `infrastructure` on exercise detail and GET .../infrastructure/exercises/:id/summary/ */
export type InfrastructureSummaryDto = IExerciseInfrastructure;

export interface InfrastructureSaveConfigurationPayload {
  exercise_id: string;
  cloud_name?: string;
  networks?: any[];
  subnets?: any[];
  routers?: any[];
  instances?: any[];
  security_groups?: any[];
}

/** GET /api/infrastructure/openstack/flavors/ */
export interface OpenstackFlavorRowDto {
  id: string;
  name: string;
  vcpus?: number | null;
  ram_mb?: number | null;
  disk_gb?: number | null;
  is_public?: boolean | null;
}

/** GET /api/infrastructure/openstack/images/ */
export interface OpenstackImageRowDto {
  id: string;
  name: string;
  size_bytes?: number | null;
  visibility?: string | null;
  status?: string | null;
}

export interface OpenstackFlavorsResponseDto {
  cloud_name: string;
  flavors: OpenstackFlavorRowDto[];
}

export interface OpenstackImagesResponseDto {
  cloud_name: string;
  images: OpenstackImageRowDto[];
}

/** Optional ``cloud_name`` query param; omit to use server default cloud. */
export interface OpenstackCatalogQueryArg {
  cloudName?: string;
}

/** GET/PATCH `/api/exercises/:id/approve` (Django exercise app). */
export interface ExerciseApproveStateDto {
  exercise_id: string;
  is_approved: boolean;
  is_prepared: boolean;
  can_approve: boolean;
  prepare_validation_error: string | null;
}

/** GET `/api/exercises/form-meta` */
export interface ExerciseFormMetaDto {
  categories: { id: number; name: string }[];
  types: { id: number; name: string; kind: string }[];
  severities: { id: number; name: string; code: string }[];
  modes: { id: number; name: string; code: string }[];
}

/** POST `/api/exercises` */
export interface CreateExercisePayload {
  name: string;
  category: number;
  type: number;
  severity?: number | null;
  mode?: number | null;
  description?: string;
  objective?: string;
  prerequisite?: string;
  /** When set to a `File`, the request is sent as `multipart/form-data`. */
  thumbnail?: File | null;
}

/** Build multipart body for create/update when a thumbnail file is included. */
function buildExerciseFormData(body: CreateExercisePayload): FormData {
  const fd = new FormData();
  fd.append('name', body.name);
  fd.append('category', String(body.category));
  fd.append('type', String(body.type));
  fd.append('severity', body.severity != null ? String(body.severity) : '');
  fd.append('mode', body.mode != null ? String(body.mode) : '');
  fd.append('description', body.description ?? '');
  fd.append('objective', body.objective ?? '');
  fd.append('prerequisite', body.prerequisite ?? '');
  if (body.thumbnail instanceof File) {
    fd.append('thumbnail', body.thumbnail);
  }
  return fd;
}

/** PATCH `/api/exercises/:id/detail` — same body shape as create (partial updates supported server-side). */
export type UpdateExercisePayload = CreateExercisePayload;

export interface InfrastructureSaveConfigurationResponseDto {
  detail: string;
  result: unknown;
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: AppConfig.apiBaseUrl,
    prepareHeaders: (headers) => {
      if (typeof window !== 'undefined') {
        const token = window.localStorage.getItem('access_token');
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      }
      return headers;
    },
  }),
  tagTypes: [
    'Users',
    'Roles',
    'Categories',
    'Scenarios',
    'ScenarioDetail',
    'Infrastructure',
    'ExerciseApprove',
    'ExerciseActiveRun',
    'ExerciseActiveConsole',
  ],
  endpoints: (builder) => ({
    listUsers: builder.query<
      UsersListResponseDto,
      { q?: string; team?: string; status?: string; page?: string | number; sort?: string } | void
    >({
      query: (params) => ({
        url: '/api/users/',
        params: {
          q: params?.q,
          team: params?.team,
          status: params?.status,
          page: params?.page,
          sort: params?.sort ?? 'score_desc',
        },
      }),
      providesTags: ['Users'],
    }),

    listRoles: builder.query<RoleDto[], void>({
      query: () => ({ url: '/api/teams/' }),
      providesTags: ['Roles'],
    }),

    createRole: builder.mutation<RoleDto, { name: string; code?: string; is_active?: boolean; color_code?: string }>({
      query: (body) => ({
        url: '/api/teams/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Roles'],
    }),

    updateRole: builder.mutation<RoleDto, { id: number; name?: string; code?: string; is_active?: boolean; color_code?: string }>({
      query: ({ id, ...patch }) => ({
        url: `/api/teams/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Roles'],
    }),

    listCategories: builder.query<
      CategoryListResponseDto,
      { status?: string; q?: string; page?: number; page_size?: number } | void
    >({
      query: (params) => ({
        url: '/api/categories/',
        params: {
          status: params?.status,
          q: params?.q,
          page: params?.page,
          page_size: params?.page_size,
        },
      }),
      transformResponse: (response: CategoryDto[] | CategoryListResponseDto) => {
        if (Array.isArray(response)) {
          return {
            results: response,
            count: response.length,
            next: null,
            previous: null,
            current_page: 1,
            total_pages: 1,
          };
        }
        return response;
      },
      providesTags: ['Categories'],
    }),

    createCategory: builder.mutation<
      CategoryDto,
      { name: string; slug?: string | null; is_published?: boolean; is_featured?: boolean; is_disabled?: boolean; order?: number }
    >({
      query: (body) => ({
        url: '/api/categories/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Categories'],
    }),

    updateCategory: builder.mutation<
      CategoryDto,
      { id: number; name?: string; slug?: string | null; is_published?: boolean; is_featured?: boolean; is_disabled?: boolean; order?: number }
    >({
      query: ({ id, ...patch }) => ({
        url: `/api/categories/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Categories'],
    }),

    deleteCategory: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/categories/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Categories'],
    }),

    /** Superuser toggle from Django admin. When false, scenario delete stays hidden and is rejected. */
    getScenarioUiSettings: builder.query<{ show_scenario_delete_button: boolean }, void>({
      query: () => ({ url: '/api/exercises/ui-settings' }),
    }),

    deleteScenario: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/exercises/${id}/detail`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Scenarios', id: 'LIST' },
        { type: 'Scenarios', id },
        { type: 'ScenarioDetail', id },
        { type: 'ExerciseApprove', id },
        { type: 'Infrastructure', id },
      ],
    }),

    listScenarios: builder.query<
      ScenarioListResponseDto,
      { q?: string; category?: string | number; ttx_enabled?: boolean | string; is_approved?: boolean | string; page?: string | number } | void
    >({
      query: (params) => ({
        url: '/api/exercises',
        params: {
          q: params?.q,
          category: params?.category,
          ttx_enabled: params?.ttx_enabled,
          is_approved: params?.is_approved,
          page: params?.page,
        },
      }),
      transformResponse: (response: ScenarioListItemDto[] | ScenarioListResponseDto) => {
        if (Array.isArray(response)) {
          return {
            results: response,
            count: response.length,
            next: null,
            previous: null,
            current_page: 1,
            total_pages: 1,
          };
        }
        return response;
      },
      providesTags: (result) => {
        const listTag = [{ type: 'Scenarios' as const, id: 'LIST' }];
        if (!result?.results) return listTag;
        return [
          ...listTag,
          ...result.results.map((item) => ({ type: 'Scenarios' as const, id: item.id })),
        ];
      },
    }),

    getScenarioDetail: builder.query<ScenarioDetailDto, string>({
      query: (id) => ({ url: `/api/exercises/${id}/detail` }),
      providesTags: (_result, _error, id) => [{ type: 'ScenarioDetail', id }],
    }),

    /** Prepare validation + can_approve for the Approve / Go Live button. */
    getExerciseApproveState: builder.query<ExerciseApproveStateDto, string>({
      query: (id) => ({ url: `/api/exercises/${id}/approve` }),
      providesTags: (_result, _error, id) => [{ type: 'ExerciseApprove', id }],
    }),

    getExerciseFormMeta: builder.query<ExerciseFormMetaDto, void>({
      query: () => ({ url: '/api/exercises/form-meta' }),
    }),

    createExercise: builder.mutation<ScenarioDetailDto, CreateExercisePayload>({
      query: (body) => {
        if (body.thumbnail instanceof File) {
          return {
            url: '/api/exercises',
            method: 'POST',
            body: buildExerciseFormData(body),
          };
        }
        const { thumbnail: _thumb, ...rest } = body;
        return {
          url: '/api/exercises',
          method: 'POST',
          body: rest,
        };
      },
      invalidatesTags: [{ type: 'Scenarios', id: 'LIST' }],
    }),

    updateExercise: builder.mutation<ScenarioDetailDto, { id: string; body: UpdateExercisePayload }>({
      query: ({ id, body }) => {
        if (body.thumbnail instanceof File) {
          return {
            url: `/api/exercises/${id}/detail`,
            method: 'PATCH',
            body: buildExerciseFormData(body),
          };
        }
        const { thumbnail: _thumb, ...rest } = body;
        return {
          url: `/api/exercises/${id}/detail`,
          method: 'PATCH',
          body: rest,
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ScenarioDetail', id: arg.id },
        { type: 'Scenarios', id: 'LIST' },
        { type: 'Scenarios', id: arg.id },
        { type: 'ExerciseApprove', id: arg.id },
      ],
    }),

    setScenarioTtx: builder.mutation<
      { scenario_id: string; ttx_enabled: boolean; is_active: boolean },
      { id: string; ttx_enabled: boolean }
    >({
      query: ({ id, ttx_enabled }) => ({
        url: `/api/exercises/${id}/ttx`,
        method: 'PATCH',
        body: { ttx_enabled },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ScenarioDetail', id: arg.id },
        { type: 'Scenarios', id: arg.id },
        { type: 'Scenarios', id: 'LIST' },
      ],
    }),

    setScenarioPrepared: builder.mutation<
      { exercise_id: string; is_prepared: boolean; is_approved: boolean },
      { id: string; is_prepared: boolean }
    >({
      query: ({ id, is_prepared }) => ({
        url: `/api/exercises/${id}/prepare`,
        method: 'PATCH',
        body: { is_prepared },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ScenarioDetail', id: arg.id },
        { type: 'ExerciseApprove', id: arg.id },
        { type: 'Scenarios', id: arg.id },
        { type: 'Scenarios', id: 'LIST' },
      ],
    }),

    /** Approve / revoke approval (same prepare validation as backend `ExerciseApproveAPIView`). */
    setScenarioApproval: builder.mutation<ExerciseApproveStateDto, { id: string; is_approved: boolean }>({
      query: ({ id, is_approved }) => ({
        url: `/api/exercises/${id}/approve`,
        method: 'PATCH',
        body: { is_approved },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ScenarioDetail', id: arg.id },
        { type: 'ExerciseApprove', id: arg.id },
        { type: 'Scenarios', id: arg.id },
        { type: 'Scenarios', id: 'LIST' },
      ],
    }),

    getInfrastructureSummary: builder.query<InfrastructureSummaryDto, string>({
      query: (exerciseId) => ({
        url: `/api/infrastructure/exercises/${exerciseId}/summary/`,
      }),
      providesTags: (_result, _error, id) => [
        { type: 'Infrastructure', id },
        { type: 'ScenarioDetail', id },
      ],
    }),

    saveInfrastructureConfiguration: builder.mutation<
      InfrastructureSaveConfigurationResponseDto,
      InfrastructureSaveConfigurationPayload
    >({
      query: ({ exercise_id, ...body }) => ({
        url: `/api/infrastructure/exercises/${exercise_id}/configuration/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Infrastructure', id: arg.exercise_id },
        { type: 'ScenarioDetail', id: arg.exercise_id },
        { type: 'ExerciseApprove', id: arg.exercise_id },
      ],
    }),

    deleteInfrastructureResource: builder.mutation<
      void,
      { exercise_id: string; resource_type: string; resource_id: string }
    >({
      query: ({ exercise_id, resource_type, resource_id }) => ({
        url: `/api/infrastructure/exercises/${exercise_id}/resources/${resource_type}/${resource_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Infrastructure', id: arg.exercise_id },
        { type: 'ScenarioDetail', id: arg.exercise_id },
        { type: 'ExerciseApprove', id: arg.exercise_id },
      ],
    }),

    getOpenstackFlavors: builder.query<OpenstackFlavorsResponseDto, OpenstackCatalogQueryArg | void>({
      query: (arg) => ({
        url: '/api/infrastructure/openstack/flavors/',
        params: {
          ...(arg?.cloudName ? { cloud_name: arg.cloudName } : {}),
        },
      }),
    }),

    getOpenstackImages: builder.query<OpenstackImagesResponseDto, OpenstackCatalogQueryArg | void>({
      query: (arg) => ({
        url: '/api/infrastructure/openstack/images/',
        params: {
          ...(arg?.cloudName ? { cloud_name: arg.cloudName } : {}),
        },
      }),
    }),

    /** GET `/api/scenarios/active/:id` — exercise run session (merged scenario + participants). */
    getExerciseActiveRun: builder.query<ExerciseRunResponse, string>({
      query: (activeId) => ({ url: apiRoutes.dashboard.activeRunDetail(activeId) }),
      providesTags: (_result, _error, activeId) => [{ type: 'ExerciseActiveRun', id: activeId }],
    }),

    launchExerciseActiveRun: builder.mutation<
      ExerciseRunResponse,
      { exerciseId: string; runId: number; activeId: string }
    >({
      query: ({ exerciseId, runId }) => ({
        url: apiRoutes.dashboard.exerciseRuns.start(exerciseId, runId),
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExerciseActiveRun', id: arg.activeId },
        { type: 'ExerciseActiveConsole', id: arg.activeId },
      ],
    }),

    startExerciseRunScenario: builder.mutation<ExerciseRunResponse, { runId: number; activeId: string }>({
      query: ({ runId }) => ({
        url: apiRoutes.dashboard.exerciseRuns.startScenario(runId),
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExerciseActiveRun', id: arg.activeId },
        { type: 'ExerciseActiveConsole', id: arg.activeId },
      ],
    }),

    finalizeExerciseRunParticipants: builder.mutation<ExerciseRunResponse, { runId: number; activeId: string }>({
      query: ({ runId }) => ({
        url: apiRoutes.dashboard.exerciseRuns.finalizeParticipants(runId),
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExerciseActiveRun', id: arg.activeId },
        { type: 'ExerciseActiveConsole', id: arg.activeId },
      ],
    }),

    endActiveScenario: builder.mutation<void, { activeId: string }>({
      query: ({ activeId }) => ({
        url: apiRoutes.dashboard.scenarios.actives.end(activeId),
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExerciseActiveRun', id: arg.activeId },
        { type: 'ExerciseActiveConsole', id: arg.activeId },
      ],
    }),

    sendActiveScenarioInject: builder.mutation<unknown, { activeId: string; injectId: number }>({
      query: ({ activeId, injectId }) => ({
        url: apiRoutes.dashboard.scenarios.actives.sendInject(activeId, injectId),
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExerciseActiveRun', id: arg.activeId },
        { type: 'ExerciseActiveConsole', id: arg.activeId },
      ],
    }),

    /** GET `/api/exercises/active/:id/console` — VNC URL + participant list for dashboard console. */
    getExerciseActiveConsole: builder.query<
      ConsoleData,
      { activeId: string; participant?: string | null }
    >({
      query: ({ activeId, participant }) => {
        const raw = participant?.trim();
        const valid =
          raw && raw !== 'undefined' && raw !== 'null' && raw !== 'NaN';
        return {
          url: apiRoutes.dashboard.scenarios.actives.console(activeId),
          params: valid ? { participant: raw } : undefined,
        };
      },
      providesTags: (_result, _error, arg) => [
        { type: 'ExerciseActiveConsole', id: arg.activeId },
        { type: 'ExerciseActiveConsole', id: `${arg.activeId}:${arg.participant ?? ''}` },
      ],
    }),

    /** GET `/api/exercises/active/:id/console/status` — Nova status, no new VNC token. */
    getExerciseActiveConsoleVmStatus: builder.query<
      { vm_status: string; run_participant_id: number },
      { activeId: string; participant?: string | null }
    >({
      query: ({ activeId, participant }) => {
        const raw = participant?.trim();
        const valid =
          raw && raw !== 'undefined' && raw !== 'null' && raw !== 'NaN';
        return {
          url: apiRoutes.dashboard.scenarios.actives.vmStatus(activeId),
          params: valid ? { participant: raw } : undefined,
        };
      },
    }),

    /** POST `/api/exercises/active/:id/console/soft-reboot` — Nova ACPI reboot of the console VM. */
    softRebootExerciseActiveConsole: builder.mutation<
      {
        detail: string;
        reboot_type: string;
        server_id: string;
        server_name: string;
        previous_status: string;
        run_participant_id: number;
      },
      { activeId: string; participant?: string | null }
    >({
      query: ({ activeId, participant }) => {
        const raw = participant?.trim();
        const valid =
          raw && raw !== 'undefined' && raw !== 'null' && raw !== 'NaN';
        return {
          url: apiRoutes.dashboard.scenarios.actives.softReboot(activeId),
          method: 'POST',
          body: {},
          params: valid ? { participant: raw } : undefined,
        };
      },
    }),
  }),
});

export const {
  useListUsersQuery,
  useListRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useListCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetScenarioUiSettingsQuery,
  useDeleteScenarioMutation,
  useListScenariosQuery,
  useGetScenarioDetailQuery,
  useGetExerciseApproveStateQuery,
  useGetExerciseFormMetaQuery,
  useCreateExerciseMutation,
  useUpdateExerciseMutation,
  useSetScenarioTtxMutation,
  useSetScenarioPreparedMutation,
  useSetScenarioApprovalMutation,
  useGetInfrastructureSummaryQuery,
  useSaveInfrastructureConfigurationMutation,
  useDeleteInfrastructureResourceMutation,
  useGetOpenstackFlavorsQuery,
  useGetOpenstackImagesQuery,
  useGetExerciseActiveRunQuery,
  useLaunchExerciseActiveRunMutation,
  useStartExerciseRunScenarioMutation,
  useFinalizeExerciseRunParticipantsMutation,
  useEndActiveScenarioMutation,
  useSendActiveScenarioInjectMutation,
  useGetExerciseActiveConsoleQuery,
  useLazyGetExerciseActiveConsoleVmStatusQuery,
  useSoftRebootExerciseActiveConsoleMutation,
} = apiSlice;

